import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
} from '@sunwisesoftware/sunwise-ui';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LabelError, TitleWithDetail } from 'common/components';
import AccordionCard from 'common/components/accordions/AccordionCard';
import ReactHookFormIntlNumberInput from 'common/components/form/ReactHookFormIntlNumberInput';
import {
    calculateRangesPriceFrom,
    calculateRangesPriceTo,
    calculateRangesPriceToByNextRange,
} from 'common/utils/helpers';

const OpexArrayRanges = ({
    control,
    currencyIso,
    currencySymbol,
    disabled,
    getValues,
    name,
    setValue,
    title,
}) => {
    const { t } = useTranslation();

    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });

    const [costRanges] = useWatch({
        control,
        name: [name],
    });

    const handleOnChange = (i) => {
        let lastMax = costRanges[i]?.max;

        for (let j = i + 1; j < costRanges.length; j++) {
            let range = costRanges[j];
            if (!isNull(range)) {
                let lastMin = Number(range.min);

                if (Number(lastMax) !== lastMin - 1) {
                    lastMin = calculateRangesPriceFrom(lastMax);

                    setValue(`${name}.${j}.min`, lastMin);
                }
                if (Number(lastMin) > Number(range.max)) {
                    lastMax = calculateRangesPriceTo(lastMin);

                    setValue(`${name}.${j}.max`, lastMax);
                } else {
                    lastMax = range.max;
                }
            }
        }
    };

    const handleNewItem = (lastField) => {
        const newMin = !isUndefined(lastField)
            ? calculateRangesPriceFrom(lastField.max)
            : 1;
        const newMax = calculateRangesPriceTo(newMin);

        append({
            amount: 0,
            max: newMax,
            min: newMin,
        });
    };

    const handleRemove = (index) => {
        if (index === 1) {
            setValue(`${name}.0.min`, 1);
            setValue(`${name}.0.max`, 10);
        }
        remove(index);

        setValue(name, getValues(name));
    };

    const validateToPriceField = (event, value, i) => {
        event.preventDefault();

        const currentRange = getValues(`${name}.${i}`);

        if (!value || parseInt(currentRange.min) > parseInt(value)) {
            let lastMax = calculateRangesPriceTo(currentRange.min);

            const nextRange = getValues(`${name}.${i + 1}`);
            if (nextRange)
                lastMax = calculateRangesPriceToByNextRange(nextRange);

            setValue(`${name}.${i}.max`, lastMax);
        }
    };

    const handleEnterKey = (event, value, i) => {
        if (event.key === 'Enter') validateToPriceField(event, value, i);
    };

    return (
        <AccordionCard
            defaultExpanded
            customTitle={
                <TitleWithDetail variant="body2">{title}</TitleWithDetail>
            }
            sxCard={{ boxShadow: 'none', mb: 0 }}
        >
            {fields.map((item, i) => (
                <Grid container mt={i > 0 ? 1 : 0} mb={1} key={item.id}>
                    <Grid size={{ md: 6, xs: 18 }}>
                        <ReactHookFormIntlNumberInput
                            allowNegativeValue={false}
                            control={control}
                            disabled
                            fullWidth
                            label={t('Minimum')}
                            name={`${name}.${i}.min`}
                        />
                    </Grid>
                    <Grid size={{ md: 6, xs: 18 }}>
                        <ReactHookFormIntlNumberInput
                            allowNegativeValue={false}
                            allowDecimals={false}
                            control={control}
                            disabled={disabled}
                            fullWidth
                            label={t('Maximum')}
                            name={`${name}.${i}.max`}
                            onBlur={({ event, value }) => {
                                validateToPriceField(event, value, i);
                                handleOnChange(i);
                            }}
                            onKeyDown={({ event, value }) =>
                                handleEnterKey(event, value, i)
                            }
                        />
                    </Grid>
                    <Grid size={{ md: 6, xs: 18 }}>
                        <Box
                            sx={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: '5px',
                            }}
                        >
                            <ReactHookFormIntlNumberInput
                                allowNegativeValue={false}
                                append={currencyIso}
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={t('Cost')}
                                min="0"
                                name={`${name}.${i}.amount`}
                                prepend={currencySymbol}
                            />
                            <IconButton
                                disabled={disabled}
                                onClick={() => handleRemove(i)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                    <Grid size={18}>
                        <Divider />
                    </Grid>
                </Grid>
            ))}

            {fields && fields.length === 0 && (
                <Grid container mt={2}>
                    <Grid size={{ xs: 'grow' }}>
                        <LabelError type="error">
                            {t('Enter at least one range')}
                        </LabelError>
                    </Grid>
                </Grid>
            )}

            {fields && fields.length >= 0 && (
                <Grid container mt={2}>
                    <Grid size={{ xs: 18 }} textAlign="right">
                        <Button
                            endIcon={<AddIcon />}
                            disabled={disabled}
                            onClick={() =>
                                handleNewItem(
                                    getValues(`${name}.${fields.length - 1}`),
                                )
                            }
                            sx={{ width: { lg: 'auto', xs: '100%' } }}
                            type="button"
                            variant="outlined"
                        >
                            {t('Add range')}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </AccordionCard>
    );
};

OpexArrayRanges.propTypes = {
    control: PropTypes.object,
    currencyIso: PropTypes.string,
    currencySymbol: PropTypes.string,
    disabled: PropTypes.bool,
    getValues: PropTypes.func,
    name: PropTypes.string,
    setValue: PropTypes.func,
    title: PropTypes.string,
};

export default OpexArrayRanges;
